/**
 * Group consecutive "fehlende Einträge" into Doppelstunde blocks.
 *
 * Mirrors `groupDiaryLessons` in the Klassenbuch diary view: a run of
 * open entries on the same calendar day with the same class, subject,
 * substitution-flag and back-to-back schedule periods (N then N+1) gets
 * folded into a single visual row. The teacher's "Unterrichtsinhalt"
 * inline editor in that row then writes to every member lesson at once,
 * and the Anwesenheit button opens the first lesson (the AttendanceDialog
 * already auto-projects to the consecutive lesson on save).
 *
 * @module klassenbuch-kiosk/utils/openEntryGroups
 */

import type { KioskOpenEntry } from "../types/chipTypes";

export interface KioskOpenEntryGroup {
  /** Stable React key — the first member's lesson_id. */
  key: string;
  /** Member entries in ascending period order (period N, N+1, …). */
  entries: KioskOpenEntry[];
}

/** Calendar-day key (local) so entries only merge within the same day. */
function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** True when `next` continues `prev` as the same Doppelstunde block:
 *  same class + subject + substitution-flag, same day, on the period
 *  immediately following. Holiday / cancelled entries are already gone
 *  by the time we see this list (filtered server-side in
 *  fn_klassenbuch_get_open_entries), so no holiday guard is needed. */
function canMerge(prev: KioskOpenEntry, next: KioskOpenEntry): boolean {
  if (!prev.class_id || !next.class_id || prev.class_id !== next.class_id) {
    return false;
  }
  if (prev.subject_id !== next.subject_id) return false;
  if (!!prev.is_substitution !== !!next.is_substitution) return false;
  if (dayKey(prev.start_at) !== dayKey(next.start_at)) return false;
  if (prev.period_number == null || next.period_number == null) return false;
  return next.period_number === prev.period_number + 1;
}

/**
 * Group the open-entries list into Doppelstunde blocks.
 *
 * The RPC returns entries ordered by `end_at DESC` (newest first). We
 * re-sort ascending within each day for the merge pass, then return the
 * groups newest-first so the rendered table still reads "most recent at
 * the top" like the un-grouped version.
 */
export function groupKioskOpenEntries(
  entries: readonly KioskOpenEntry[],
): KioskOpenEntryGroup[] {
  if (entries.length === 0) return [];

  // Ascending sort by (day, period) so adjacent entries are merge candidates.
  const ascending = [...entries].sort((a, b) => {
    if (a.start_at < b.start_at) return -1;
    if (a.start_at > b.start_at) return 1;
    return (a.period_number ?? 0) - (b.period_number ?? 0);
  });

  const groups: KioskOpenEntryGroup[] = [];
  let run: KioskOpenEntry[] = [];
  for (const entry of ascending) {
    if (run.length === 0) {
      run = [entry];
      continue;
    }
    if (canMerge(run[run.length - 1], entry)) {
      run.push(entry);
    } else {
      groups.push({ key: run[0].lesson_id, entries: run });
      run = [entry];
    }
  }
  if (run.length > 0) groups.push({ key: run[0].lesson_id, entries: run });

  // Newest first for display — sort by the LATEST member's end_at.
  groups.sort((a, b) => {
    const aEnd = a.entries[a.entries.length - 1].end_at;
    const bEnd = b.entries[b.entries.length - 1].end_at;
    if (aEnd > bEnd) return -1;
    if (aEnd < bEnd) return 1;
    return 0;
  });
  return groups;
}
